import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { Btn } from "../UI";
import { SignaturePromptModal } from "../SignaturePromptModal";
import { AlertTriangle, XCircle, X, PenLine, Loader2 } from "lucide-react";

const TYPE_LABEL = { allergy: "Allergy", duplicate: "Duplicate", review: "Library review" };

/** Runs the safety checks, collects acknowledgements, then applies the prescriber's e-signature. */
export function RxSignDialog({ rx, open, onClose, onSigned }) {
  const [chk, setChk] = useState(null);
  const [ack, setAck] = useState({});
  const [notes, setNotes] = useState({});
  const [sigOpen, setSigOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setChk(null); setAck({}); setNotes({});
    api.post(`/prescriptions/${rx.id}/check`).then((r) => setChk(r.data)).catch((e) => { toast.error(apiError(e)); onClose(); });
  }, [open, rx.id]); // eslint-disable-line
  if (!open) return null;
  const noteOk = (w) => w.type !== "allergy" || (notes[w.key] || "").trim().length >= 5;
  const ready = chk && chk.ok && chk.warnings.every((w) => ack[w.key] && noteOk(w));

  const sign = async ({ image, name }) => {
    setBusy(true);
    try {
      const r = await api.put(`/prescriptions/${rx.id}/sign`, { image, name, acknowledged: Object.keys(ack).filter((k) => ack[k]), override_notes: notes });
      toast.success(`${rx.rx_number} signed`);
      setSigOpen(false); onSigned(r.data);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-[#3E2723]/40 p-4 backdrop-blur-sm" data-testid="rx-sign-dialog">
      <div className="max-h-[85vh] w-full max-w-xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div><h3 className="font-heading text-lg font-bold text-[#3E2723]">Review and sign {rx.rx_number}</h3>
            <p className="text-xs text-[#7B6157]">Safety checks run against the client's allergies, active medications, and the Medication Library.</p></div>
          <button type="button" data-testid="rx-sign-close" aria-label="Close" onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-full text-[#7B6157] hover:bg-[#F5F2EB]"><X size={16} /></button>
        </div>
        {!chk && <div className="flex items-center gap-2 py-6 text-sm text-[#7B6157]"><Loader2 size={14} className="animate-spin" /> Running safety checks</div>}
        {chk && (
          <div className="space-y-4">
            {chk.errors.length > 0 && (
              <div className="rounded-xl border border-[#FFCDD2] bg-[#FFEBEE] p-3" data-testid="rx-check-errors">
                <div className="mb-1 flex items-center gap-1.5 text-xs font-bold uppercase text-[#B71C1C]"><XCircle size={13} /> Fix before signing</div>
                <ul className="list-disc space-y-0.5 pl-5 text-sm text-[#B71C1C]">{chk.errors.map((e) => <li key={e}>{e}</li>)}</ul>
              </div>
            )}
            {chk.warnings.length > 0 && (
              <div className="space-y-2" data-testid="rx-check-warnings">
                <div className="flex items-center gap-1.5 text-xs font-bold uppercase text-[#8D5A00]"><AlertTriangle size={13} /> Acknowledge each warning</div>
                {chk.warnings.map((w, i) => (
                  <div key={w.key} className="rounded-xl border border-[#FFE0B2] bg-[#FFF8E1] p-3 text-sm text-[#5D4037]">
                    <label className="flex items-start gap-2.5">
                      <input type="checkbox" data-testid={`rx-warning-ack-${i}`} checked={!!ack[w.key]} onChange={(e) => setAck((s) => ({ ...s, [w.key]: e.target.checked }))} className="mt-0.5 h-4 w-4 accent-[#4E342E]" />
                      <span><span className="mr-1.5 rounded-full bg-white px-1.5 py-0.5 text-[9px] font-bold uppercase text-[#8D5A00]">{TYPE_LABEL[w.type]}</span>{w.message}. {w.type === "allergy" ? "I want to override this allergy warning and prescribe anyway." : "I reviewed this and still intend to prescribe."}</span>
                    </label>
                    {w.type === "allergy" && ack[w.key] && (
                      <div className="mt-2 pl-6"><label className="mb-1 block text-[10px] font-bold uppercase text-[#7B6157]" htmlFor={`rx-warning-note-${i}`}>Override note (required, at least 5 characters)</label>
                        <textarea id={`rx-warning-note-${i}`} data-testid={`rx-warning-note-${i}`} rows={2} value={notes[w.key] || ""} onChange={(e) => setNotes((s) => ({ ...s, [w.key]: e.target.value }))}
                          placeholder="For example, reported reaction was mild nausea, benefit outweighs risk, monitor closely"
                          className="w-full rounded-lg border border-[#E5DFD3] bg-white px-3 py-2 text-sm outline-none focus:border-[#8D6E63]" />
                        <p className="text-[10px] text-[#7B6157]">Saved with the signed prescription and the audit log.</p></div>
                    )}
                  </div>
                ))}
              </div>
            )}
            {chk.ok && !chk.warnings.length && <p className="rounded-xl bg-[#E8F5E9] p-3 text-sm text-[#2E7D32]" data-testid="rx-check-clear">No safety warnings found.</p>}
            <div className="flex justify-end gap-2 pt-2">
              <Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn>
              <Btn type="button" data-testid="rx-sign-btn" disabled={!ready || busy} onClick={() => setSigOpen(true)}><PenLine size={14} /> Sign prescription</Btn>
            </div>
          </div>
        )}
      </div>
      <SignaturePromptModal open={sigOpen} onClose={() => setSigOpen(false)} onConfirm={sign} busy={busy}
        title={`Sign ${rx.rx_number}`} subtitle="Your e-signature locks this prescription and adds it to the client's medication history." />
    </div>
  );
}
